import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

import { apiGetSymptomsOptions } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { useSupportedSymptoms } from "../../../catalogs/supported-symptoms";
import { Button } from "../../../components/ui-kit/button";
import { SymptomPlotModel } from "../../../models/plotting";
import { PatientSubpage } from "../common/patient-subpage";
import { PlotCanvas, PlotControls, usePlot } from "../common/plot";
import { AddSymptomsDialog } from "./add-symptoms-dialog";
import { Symptoms, SymptomsItem } from "./symptoms";
import { SymptomsTable } from "./symptoms-table";

export const SymptomsRoute = observer(function SymptomsRoute(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  const api = props.app.seerlinqApi;
  const supportedSymptoms = useSupportedSymptoms();
  const [model] = useState(
    () => new Symptoms([], api.permissions, api.requireUserUUID()),
  );

  const [showAddDialog, setShowAddDialog] = useState(false);

  // The generic list rather than `hf/symptoms`, which serves only the two
  // HF-validation study symptoms: the page shows every catalog symptom the
  // patient has, including the ones they reported from the app themselves.
  const { data: response, refetch } = useQuery(
    apiGetSymptomsOptions({ query: { patient_ids: [patient.patientId] } }),
  );
  const data = response?.symptoms ?? noSymptoms;

  useEffect(() => {
    if (response) model.updateData(response.symptoms);
  }, [response, model]);

  // The whole catalog, in its own order: the plot model keeps only the names
  // that actually have enough datapoints, so a patient with two symptoms still
  // gets two series.
  const plot = usePlot(
    () =>
      new SymptomPlotModel(
        model,
        supportedSymptoms.map((s) => s.data_name),
        "plot-canvas",
      ),
  );

  return (
    <PatientSubpage title={t`Symptoms`} loading={!model.initialized}>
      <PlotCanvas model={plot} />

      <SymptomsTable
        filterActions={
          <>
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => setShowAddDialog(true)}
            >
              <Trans>Add symptoms</Trans>
            </Button>
            <PlotControls model={plot} />
          </>
        }
        api={api}
        data={data}
        onInvalidated={refetch}
        patient={patient}
        persistState
      />

      {showAddDialog && (
        <AddSymptomsDialog
          app={props.app}
          onClose={() => setShowAddDialog(false)}
          onSaved={refetch}
        />
      )}
    </PatientSubpage>
  );
});

const noSymptoms: SymptomsItem[] = [];
